import { useEffect, useState } from "react";
import { eventsApiBaseUrl } from "../config/eventsApi";
import { resolveMediaUrl } from "./publicGalleryApi";

const FETCH_TIMEOUT_MS = 8000;
const SLUG_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,80}$/;

/** How many published pages sit in the header before the rest fold into More. */
export const NAV_PAGE_INLINE_LIMIT = 2;

export function pageSlugFromPath(path) {
  const match = String(path || "").match(/^\/p\/([^/]+)\/?$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** https, site path, mailto, or tel. Anything else is dropped. */
export function safePageHref(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw || raw.startsWith("//")) return "";
  if (raw.startsWith("/") && !raw.startsWith("/\\")) return raw;
  if (/^(mailto:|tel:)/i.test(raw)) return raw;
  if (!/^https:\/\//i.test(raw)) return "";
  try {
    const url = new URL(raw);
    if (!url.hostname || url.username || url.password) return "";
    return raw;
  } catch {
    return "";
  }
}

function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeBlock(block) {
  if (!block || typeof block !== "object") return null;
  if (block.type === "heading") {
    const heading = text(block.text);
    return heading ? { type: "heading", text: heading } : null;
  }
  if (block.type === "text") {
    const markdown = typeof block.markdown === "string" ? block.markdown : "";
    return markdown.trim() ? { type: "text", markdown } : null;
  }
  if (block.type === "image") {
    const url = resolveMediaUrl(block.url);
    if (!url) return null;
    return {
      type: "image",
      url,
      alt: text(block.alt),
      caption: text(block.caption),
    };
  }
  if (block.type === "button") {
    const label = text(block.label);
    const url = safePageHref(block.url);
    if (!label || !url) return null;
    return {
      type: "button",
      label,
      url,
      style: block.style === "secondary" ? "secondary" : "primary",
    };
  }
  if (block.type === "gallery") {
    const albumSlug = text(block.albumSlug);
    return albumSlug ? { type: "gallery", albumSlug } : null;
  }
  if (block.type === "divider") return { type: "divider" };
  return null;
}

export function normalizeNavPage(raw) {
  if (!raw || typeof raw !== "object") return null;
  const slug = text(raw.slug);
  const title = text(raw.title);
  if (!SLUG_RE.test(slug) || !title) return null;
  const navOrder = Number(raw.navOrder);
  const navLabel = text(raw.navLabel);
  return {
    slug,
    title,
    description: text(raw.description),
    showInNav: raw.showInNav === true,
    navLabel: navLabel || title,
    navOrder: Number.isFinite(navOrder) ? navOrder : 0,
  };
}

export function normalizePage(raw) {
  const nav = normalizeNavPage(raw);
  if (!nav) return null;
  const coverSource =
    raw.coverImage && typeof raw.coverImage === "object"
      ? raw.coverImage.url
      : raw.coverImage;
  return {
    ...nav,
    coverImage: resolveMediaUrl(coverSource),
    blocks: (Array.isArray(raw.blocks) ? raw.blocks : [])
      .map(normalizeBlock)
      .filter(Boolean),
  };
}

export function navPagesFromList(pages) {
  return (Array.isArray(pages) ? pages : [])
    .map(normalizeNavPage)
    .filter((page) => page && page.showInNav)
    .sort((a, b) => a.navOrder - b.navOrder || a.navLabel.localeCompare(b.navLabel));
}

/** First two nav pages stay inline. A longer list folds the rest under More. */
export function partitionNavPages(pages, limit = NAV_PAGE_INLINE_LIMIT) {
  const visible = navPagesFromList(pages);
  if (visible.length <= limit) return { inline: visible, overflow: [] };
  return { inline: visible.slice(0, limit), overflow: visible.slice(limit) };
}

async function readJson(res) {
  const textBody = await res.text();
  try {
    return JSON.parse(textBody);
  } catch {
    return null;
  }
}

async function fetchJson(path, signal) {
  const base = eventsApiBaseUrl();
  if (!base) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const res = await fetch(`${base}${path}`, {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    });
    const data = await readJson(res);
    if (!res.ok || !data || data.ok !== true) return null;
    return data;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

/** Nav summaries. An unreachable API yields an empty list (no blank links). */
export async function fetchPublicPages({ signal } = {}) {
  const data = await fetchJson("/api/public/pages", signal);
  if (!data || !Array.isArray(data.pages)) return [];
  return data.pages.map(normalizeNavPage).filter(Boolean);
}

/** Full page, or null when the slug is unknown or unpublished. */
export async function fetchPublicPage(slug, { signal } = {}) {
  const wanted = text(slug);
  if (!SLUG_RE.test(wanted)) return null;
  const data = await fetchJson(
    `/api/public/pages/${encodeURIComponent(wanted)}`,
    signal,
  );
  const page = data?.page ? normalizePage(data.page) : null;
  if (!page || page.slug !== wanted) return null;
  return page;
}

export function usePublicNavPages() {
  const [pages, setPages] = useState([]);
  useEffect(() => {
    let cancel = false;
    fetchPublicPages().then((next) => {
      if (!cancel) setPages(next);
    });
    return () => {
      cancel = true;
    };
  }, []);
  return pages;
}
